import { useMemo } from "react";
import { useRouter } from "next/router";
import blogData from "@/json/blog.generated.json";
import { isProd } from "@/Env";
import { useLocale } from "@/store/locale-store";

const LOCALES = ["en", "ko", "ja", "zh", "de", "fr", "es"] as const;
export type BlogLocale = (typeof LOCALES)[number];

interface BlogPostRaw {
  slug: string;
  notionPageId: string;
  tags: string[];
  state: string;
  createTime: string | null;
  featured: number | null;
  regionsOnly: string[];
  thumbnail: string;
  subtitle: Partial<Record<BlogLocale, string>>;
  title: Partial<Record<BlogLocale, string>>;
  body: Partial<Record<BlogLocale, string>>;
}

/**
 * BlogPost — 컴포넌트가 쓰는 표면 형태. 기존 BlogPost 인터페이스와 호환.
 * - `id` = `notionPageId` (또는 slug fallback) — React key 용
 * - `url` = `notionPageId` — 라우팅 키 (`/blog/{url}`)
 * - `page` = 제목 (locale 적용)
 * - `body` = HTML 직렬화 본문 (locale 적용)
 */
export interface BlogPost {
  id: string;
  slug: string;
  url: string;
  page: string;
  subtitle: string;
  thumbnail: string;
  tag: string[];
  featured: number;
  createTime: string;
  regionsOnly: string[];
  state: string;
  body: string;
}

const items: BlogPostRaw[] = (blogData as unknown as { items: BlogPostRaw[] })
  .items;

function resolveLocale(locale?: string): BlogLocale {
  if (locale && (LOCALES as readonly string[]).includes(locale)) {
    return locale as BlogLocale;
  }
  return "en";
}

function pickLocale(
  record: Partial<Record<BlogLocale, string>>,
  lang: BlogLocale,
): string {
  return record[lang] || record.en || "";
}

function resolveOne(raw: BlogPostRaw, locale?: string): BlogPost {
  const lang = resolveLocale(locale);
  return {
    id: raw.notionPageId || raw.slug,
    slug: raw.slug,
    url: raw.notionPageId || raw.slug,
    page: pickLocale(raw.title, lang),
    subtitle: pickLocale(raw.subtitle, lang),
    thumbnail: raw.thumbnail,
    tag: raw.tags,
    featured: typeof raw.featured === "number" ? raw.featured : 9999,
    createTime: raw.createTime || "",
    regionsOnly: raw.regionsOnly,
    state: raw.state,
    body: pickLocale(raw.body, lang),
  };
}

/**
 * 발행 정책 — prod 에선 state="Done" 만 노출.
 * dev/local 에선 Writing/Done 다 보임 (작성 미리보기 용).
 */
function isPublished(raw: BlogPostRaw): boolean {
  if (!isProd) return true;
  return raw.state === "Done";
}

export function getBlogPosts(locale?: string): BlogPost[] {
  return items.filter(isPublished).map((r) => resolveOne(r, locale));
}

/**
 * notionPageId(=url) 로 단일 글 조회. 라우팅 `/blog/[id]` 와 호환.
 */
export function getBlogPostByUrl(
  url: string,
  locale?: string,
): BlogPost | null {
  const raw = items.find((i) => i.notionPageId === url || i.slug === url);
  if (!raw) return null;
  if (!isPublished(raw)) return null;
  return resolveOne(raw, locale);
}

/**
 * Notion DB의 regionsOnly 컬럼 기반 국가별 필터링:
 *  - regionsOnly 비어있음 → 모든 국가에 노출
 *  - regionsOnly에 "KR" → KR에서만 노출
 *  - regionsOnly에 "!KR" → KR 제외 모든 국가에 노출
 */
export function pickFeaturedForCountry(
  posts: BlogPost[],
  countryCode: string,
): BlogPost[] {
  const cc = countryCode?.toUpperCase() || "";
  return posts.filter((p) => {
    const regions = p.regionsOnly;
    if (!regions || regions.length === 0) return true;

    const includes = regions.filter((r) => !r.startsWith("!"));
    const excludes = regions
      .filter((r) => r.startsWith("!"))
      .map((r) => r.slice(1).toUpperCase());

    if (excludes.some((ex) => ex === cc)) return false;
    if (includes.length === 0) return true;
    return includes.some((inc) => inc.toUpperCase() === cc);
  });
}

// -------- React hooks (client) --------
// 데이터는 빌드 시 번들된 JSON에서 옴 — fetch / React Query 없음, 즉시 동기 반환.

export function useBlogPosts(): { data: BlogPost[]; isLoading: false } {
  const { locale } = useRouter();
  const data = useMemo(() => getBlogPosts(locale), [locale]);
  return { data, isLoading: false };
}

export function useBlogLatests(size: number = 4) {
  const { data } = useBlogPosts();
  const latestPosts = useMemo(() => {
    if (!data.length) return [];
    return [...data]
      .sort((a, b) => (b.createTime || "").localeCompare(a.createTime || ""))
      .slice(0, size);
  }, [data, size]);
  return { data: latestPosts, isLoading: false as const };
}

export function useFeaturedBlogs(size: number = 4) {
  const { data } = useBlogPosts();
  const { countryCode } = useLocale();
  const featuredPosts = useMemo(() => {
    if (!data.length) return [];
    const visible = pickFeaturedForCountry(data, countryCode);
    return [...visible].sort((a, b) => a.featured - b.featured).slice(0, size);
  }, [data, size, countryCode]);
  return { data: featuredPosts, isLoading: false as const };
}
